import React, {useCallback, useEffect, useState} from 'react';
import {View, StyleSheet, Alert, FlatList} from 'react-native';
import {
  Text,
  ActivityIndicator,
  Dialog,
  Portal,
  Button,
  TextInput,
  IconButton,
  Snackbar,
  Divider,
  FAB,
} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import {useInventoryStore} from '../../store/useInventoryStore';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const ReadymadeTypeListScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {readymadeTypes, isLoading, fetchReadymadeTypes, createReadymadeType, updateReadymadeType, deleteReadymadeType} =
    useInventoryStore();

  const [refreshing, setRefreshing] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [itemName, setItemName] = useState('');
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchReadymadeTypes();
  }, [fetchReadymadeTypes]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchReadymadeTypes();
    setRefreshing(false);
  }, [fetchReadymadeTypes]);

  const openAddDialog = useCallback(() => {
    setEditingItem(null);
    setItemName('');
    setDialogVisible(true);
  }, []);

  const openEditDialog = useCallback(item => {
    setEditingItem(item);
    setItemName(item.name);
    setDialogVisible(true);
  }, []);

  const handleSave = useCallback(async () => {
    const name = itemName.trim();
    if (!name) return;

    try {
      if (editingItem) {
        await updateReadymadeType(editingItem.id, {name});
      } else {
        await createReadymadeType({name});
      }
      setDialogVisible(false);
      setItemName('');
      setEditingItem(null);
    } catch (e) {
      setSnackbar(e.response?.data?.error?.message || t('common.error'));
    }
  }, [itemName, editingItem, createReadymadeType, updateReadymadeType, t]);

  const handleDelete = useCallback(
    item => {
      Alert.alert(
        t('common.delete'),
        t('inventory.deleteConfirm', {name: item.name}),
        [
          {text: t('common.cancel'), style: 'cancel'},
          {
            text: t('common.delete'),
            style: 'destructive',
            onPress: async () => {
              try {
                await deleteReadymadeType(item.id);
              } catch (e) {
                setSnackbar(
                  e.response?.data?.error?.message || t('common.error'),
                );
              }
            },
          },
        ],
      );
    },
    [deleteReadymadeType, t],
  );

  const renderItem = useCallback(
    ({item}) => {
      return (
        <View style={styles.row}>
          <Text style={styles.itemName}>{item.name}</Text>
          <View style={styles.rowActions}>
            <IconButton
              icon="pencil-outline"
              size={20}
              iconColor={colors.textSecondary}
              onPress={() => openEditDialog(item)}
              style={styles.actionBtn}
            />
            <IconButton
              icon="delete-outline"
              size={20}
              iconColor={colors.textSecondary}
              onPress={() => handleDelete(item)}
              style={styles.actionBtn}
            />
          </View>
        </View>
      );
    },
    [openEditDialog, handleDelete],
  );

  const keyExtractor = useCallback(item => String(item.id), []);

  const ListHeader = useCallback(
    () => (
      <View>
        <View style={styles.descriptionRow}>
          <Text style={styles.descriptionText}>
            {t('inventory.readymadeTypeDescription')}
          </Text>
        </View>
        <Divider />
      </View>
    ),
    [t],
  );

  const ListEmpty = useCallback(
    () => <Text style={styles.emptyText}>{t('common.noData')}</Text>,
    [t],
  );

  const ItemSeparator = useCallback(() => <Divider />, []);

  if (isLoading && readymadeTypes.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <GradientHeader title={t('inventory.readymadeTypes')} onBack={() => navigation.goBack()} />

      <FlatList
        data={readymadeTypes}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        ItemSeparatorComponent={ItemSeparator}
        refreshing={refreshing}
        onRefresh={handleRefresh}
      />

      <Portal>
        <Dialog
          visible={dialogVisible}
          onDismiss={() => setDialogVisible(false)}
          style={{borderRadius: 15}}>
          <Dialog.Title>
            {editingItem ? t('inventory.editReadymadeType') : t('inventory.addReadymadeType')}
          </Dialog.Title>
          <Dialog.Content>
            <TextInput
              label={t('inventory.typeName')}
              value={itemName}
              onChangeText={setItemName}
              mode="outlined"
              style={styles.dialogInput}
            />
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setDialogVisible(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              mode="contained"
              onPress={handleSave}
              style={styles.saveButton}>
              {t('common.save')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>

      <FAB
        icon="plus"
        style={styles.fab}
        onPress={openAddDialog}
        color={colors.white}
      />

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={3000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  loadingContainer: {flex: 1, justifyContent: 'center', alignItems: 'center'},
  descriptionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    backgroundColor: colors.surface,
  },
  descriptionText: {
    flex: 1,
    fontSize: 14,
    color: colors.textSecondary,
    lineHeight: 20,
  },
  fab: {
    position: 'absolute',
    right: spacing.md,
    bottom: spacing.md,
    backgroundColor: colors.primary,
  },
  emptyText: {
    textAlign: 'center',
    color: colors.textSecondary,
    marginTop: spacing.xxl,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.md,
  },
  itemName: {
    flex: 1,
    fontSize: 16,
    fontWeight: '600',
    color: colors.text,
  },
  rowActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionBtn: {
    margin: 0,
  },
  dialogInput: {
    marginBottom: spacing.sm,
    backgroundColor: colors.surface,
  },
  saveButton: {
    backgroundColor: colors.primary,
    borderRadius: 10,
    paddingHorizontal: 10,
  },
});

export default ReadymadeTypeListScreen;
